<template>
  <div>
    <vue-good-table
      ref="tableRef"
      :columns="enhancedColumns"
      :rows="props.rows"
      :select-options="selectOptions"
      :row-style-class="rowStyleClass"
      :theme="themeStore.theme === 'dark' ? 'nocturnal' : ''"
      :pagination-options="paginationOptions"
      :max-height="gridHeight + 'px'"
      @selected-rows-change="onSelectionChanged"
      @row-click="onRowClick"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, onBeforeUnmount, ref } from 'vue';
import { VueGoodTable } from 'vue-good-table-next'
import 'vue-good-table-next/dist/vue-good-table-next.css'
import { useThemeStore } from '../stores/themeStore'
import type { ColumnDefinition, RowClickParams, SelectionChangedParams } from '@/types/commontypes';
const tableRef = ref()
const themeStore = useThemeStore()
const gridHeight = ref(window.innerHeight); // 여유 공간 조절

const props = defineProps<{
    columns: ColumnDefinition[];
    rows: Record<string, unknown>[];
    isCheckBox?: boolean;
    setGridHeight: number | null;
    usePagination?: boolean;
    perPage?: number;
}>();

const selectedRowId = ref<number | string | unknown | null>(null)

// 컬럼 리사이즈 가능하도록 width 속성 추가
const enhancedColumns = computed(() => {
  return props.columns.map(col => ({
    ...col,
    // width가 지정되지 않은 경우 기본값 설정
    // width: col.width || '100px',
    // 컬럼 정렬 허용
    sortable: col.sortable !== false
  }))
})

// 페이지네이션 옵션
const paginationOptions = computed(() => ({
  enabled: props.usePagination !== false, // 기본값 true
  perPage: props.perPage || 10,
  perPageDropdown: [5, 10, 20, 50, 100],
  dropdownAllowAll: false,
  setCurrentPage: 1,
  nextLabel: '다음',
  prevLabel: '이전',
  rowsPerPageLabel: '페이지당 행 수',
  ofLabel: '/',
  pageLabel: '페이지',
  allLabel: '전체'
}))

const selectedRows = ref<Record<string, unknown>[]>([]);

onMounted(async () =>{
    updateGridHeight();
    window.addEventListener('resize', updateGridHeight);
    initColumnResize();
});

const selectOptions = computed(() => ({
  enabled: props.isCheckBox || false,
  selectionInfoClass: '',
  selectOnCheckboxOnly: true,
  disableSelectInfo: true,
  selectAllByGroup: false
}));

onBeforeUnmount(() => {
    window.removeEventListener('resize', updateGridHeight);
});

const updateGridHeight = () => {
  if(!props.setGridHeight)
    gridHeight.value = window.innerHeight - 300; // 필요 시 마진 조절
  else
    gridHeight.value = props.setGridHeight;
};

const onRowClick = (params: RowClickParams) => {
  selectedRowId.value = params.row.id
}

const rowStyleClass = () => {
  return 'custom-row-style';
  // return row.id === selectedRowId.value ? 'selected-row' : ''
}

const onSelectionChanged = (params: SelectionChangedParams) => {
  console.log("CommGrid 선택된 rows:", params.selectedRows);
  selectedRows.value = params.selectedRows
}

function getSelectedRows () {
  return selectedRows.value
}

// 컬럼 리사이즈 초기화
function initColumnResize() {
  setTimeout(() => {
    const table = tableRef.value?.$el?.querySelector('table')
    if (!table) return

    const headers = table.querySelectorAll('thead th')
    headers.forEach((th: HTMLElement) => {
      // 이미 리사이즈 핸들이 있으면 추가하지 않음
      if (th.querySelector('.column-resizer')) return

      // 리사이즈 핸들 추가
      const resizer = document.createElement('div')
      resizer.className = 'column-resizer'
      resizer.style.cssText = `
        position: absolute;
        top: 0;
        right: 0;
        width: 5px;
        height: 100%;
        cursor: col-resize;
        user-select: none;
        z-index: 1;
      `

      th.style.position = 'relative'
      th.appendChild(resizer)

      let startX = 0
      let startWidth = 0

      const onMouseDown = (e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        startX = e.pageX
        startWidth = th.offsetWidth

        const onMouseMove = (e: MouseEvent) => {
          e.preventDefault()
          const diff = e.pageX - startX
          const newWidth = Math.max(50, startWidth + diff)
          th.style.width = newWidth + 'px'
          th.style.minWidth = newWidth + 'px'
        }

        const onMouseUp = () => {
          document.removeEventListener('mousemove', onMouseMove)
          document.removeEventListener('mouseup', onMouseUp)
          document.body.style.cursor = ''
          document.body.style.userSelect = ''
        }

        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'

        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
      }

      resizer.addEventListener('mousedown', onMouseDown)
    })
  }, 100)
}

// expose method to parent
defineExpose({
  getSelectedRows,
  selectedRows
  });

</script>

<style>
 grid-wrapper {
  height: calc(100vh - 100px); /* 필요에 따라 nav/footer 높이만큼 빼기 */
}

/* 전체 wrapper 높이 제어 */
:deep(.vgt-wrap) {
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}

/* 테이블 영역이 남은 공간을 차지하도록 */
:deep(.vgt-wrap .vgt-responsive) {
  flex: 1 !important;
  overflow: auto !important;
  min-height: 0 !important;
}

/* 선택된 row 스타일 */
:deep(.selected-row) {
  background-color: #132bb4 !important;
  border-left: 4px solid #2196f3 !important;
}

:deep(.selected-row td) {
  font-weight: 500;
}

/* row hover 효과 */
:deep(tbody tr) {
  cursor: pointer;
  transition: all 0.2s ease;
}

:deep(tbody tr:hover) {
  background-color: #f5f5f5 !important;
}

:deep(.selected-row:hover) {
  background-color: #bbdefb !important;
}


:deep(.custom-row-style) {
  height: 10px !important; /* 행 높이 고정 */
  font-size: 12px !important;
}

:deep(.custom-row-style td) {
  height: 10px !important; /* td 높이 고정 */
  max-height: 10px !important;
  padding: 2px 8px !important; /* 패딩 축소 */
  line-height: 10px !important; /* 텍스트 세로 정렬 */
  overflow: hidden !important; /* 넘치는 내용 숨김 */
  text-overflow: ellipsis !important; /* 넘치는 텍스트 ... 표시 */
  white-space: nowrap !important; /* 줄바꿈 방지 */
}


/* 헤더 */
table.vgt-table {
    font-size: 12px;
    border-collapse: collapse;
    background-color: #FFFFFF;
    width: 100%;
    max-width: 100%;
    table-layout: auto;
    border: 1px solid #DCDFE6;
}

/* 헤더 높이 고정 및 가운데 정렬 */
:deep(table.vgt-table thead th) {
  height: 10px !important;
  max-height: 10px !important;
  padding: 2px 8px !important;
  font-size: 12px !important;
  line-height: 10px !important;
  text-align: center !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

/* 페이저 스타일 축소 */
:deep(.vgt-wrap__footer) {
  padding: 4px 8px !important;
  font-size: 11px !important;
  line-height: 1.2 !important;
  min-height: 18px !important;
  max-height: 18px !important;
  height: 18px !important;
  flex-shrink: 0 !important;
  overflow: hidden !important;
}

:deep(.vgt-wrap__footer .footer__row-count) {
  font-size: 11px !important;
  line-height: 10px !important;
  margin: 0 !important;
}

:deep(.vgt-wrap__footer .footer__navigation) {
  font-size: 11px !important;
  line-height: 10px !important;
  margin: 0 !important;
}

:deep(.vgt-wrap__footer .footer__navigation__info) {
  margin: 0 !important;
  line-height: 10px !important;
}

:deep(.vgt-wrap__footer select) {
  font-size: 11px !important;
  padding: 1px 4px !important;
  height: 20px !important;
  line-height: 1 !important;
  margin: 0 !important;
}

:deep(.vgt-wrap__footer button) {
  font-size: 11px !important;
  padding: 1px 6px !important;
  height: 20px !important;
  line-height: 1 !important;
  margin: 0 2px !important;
}

:deep(.vgt-wrap__footer .footer__navigation__page-btn) {
  padding: 1px 6px !important;
  min-width: 20px !important;
  height: 20px !important;
  line-height: 1 !important;
  margin: 0 1px !important;
}

/* 데이터 셀 정렬 옵션 */
:deep(td.text-left) {
  text-align: left !important;
}

:deep(td.text-center) {
  text-align: center !important;
}

:deep(td.text-right) {
  text-align: right !important;
}

</style>